import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../../../contexts/AuthContext';
import ImportHistoryTable from '../../../components/csv-import/ImportHistoryTable';
import { ImportHistory } from '../../../types/importHistory';
import { ImportHistoryService } from '../../../services/importHistoryService';

// Créer une instance du service
const importHistoryService = new ImportHistoryService();

/**
 * Page d'historique des importations pour l'importation de carburant
 */
const FuelImportHistoryPage: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const { currentUser, currentOrganization } = useAuth();
  
  const [imports, setImports] = useState<ImportHistory[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [selectedImportId, setSelectedImportId] = useState<string | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  
  // Charger l'historique des importations
  useEffect(() => {
    if (!currentOrganization?.id) return;
    
    const loadImportHistory = async () => {
      try {
        setLoading(true);
        const result = await importHistoryService.getImportHistory(
          currentOrganization.id,
          'fuel',
          {
            limit: rowsPerPage,
            offset: page * rowsPerPage
          }
        );
        
        setImports(result.imports);
        setTotal(result.total);
      } catch (error) {
        console.error('Erreur lors du chargement de l\'historique des importations:', error);
        setError('Impossible de charger l\'historique des importations.');
      } finally {
        setLoading(false);
      }
    };
    
    loadImportHistory();
  }, [currentOrganization?.id, page, rowsPerPage]);
  
  // Gérer le changement de page
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };
  
  // Gérer le changement du nombre d'éléments par page
  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };
  
  // Gérer le clic sur "Voir les détails"
  const handleViewDetails = (importId: string) => {
    // Stocker l'ID de l'importation dans le localStorage
    localStorage.setItem('fuelImport_historyId', importId);
    
    // Rediriger vers la page de détails
    navigate(`/admin/fuel/import/history/${importId}`);
  };
  
  // Gérer le clic sur "Télécharger le rapport"
  const handleDownloadReport = (importId: string, format: 'csv' | 'pdf') => {
    // Simuler le téléchargement d'un rapport
    console.log(`Téléchargement du rapport au format ${format} pour l'importation ${importId}`);
    
    // Dans une implémentation réelle, nous appellerions un service pour générer et télécharger le rapport
    alert(`Le rapport au format ${format} a été téléchargé.`);
  };
  
  // Gérer le clic sur "Réimporter"
  const handleReimport = (importId: string) => {
    // Rediriger vers la page d'importation avec l'ID de l'importation
    navigate(`/admin/fuel/import?reimport=${importId}`);
  };
  
  // Gérer le clic sur "Supprimer"
  const handleDelete = (importId: string) => {
    setSelectedImportId(importId);
    setOpenDeleteDialog(true);
  };
  
  // Gérer la confirmation de suppression
  const handleConfirmDelete = async () => {
    if (!selectedImportId) return;
    
    try {
      setDeleting(true);
      
      // Simuler la suppression
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Dans une implémentation réelle, nous appellerions un service pour supprimer l'importation
      // await importHistoryService.deleteImport(selectedImportId);
      
      // Mettre à jour la liste des importations
      setImports(prev => prev.filter(imp => imp.id !== selectedImportId));
      setTotal(prev => prev - 1);
      
      setOpenDeleteDialog(false);
      setSelectedImportId(null);
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'importation:', error);
      setError('Impossible de supprimer l\'importation.');
    } finally {
      setDeleting(false);
    }
  };
  
  // Gérer l'annulation de suppression
  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setSelectedImportId(null);
  };
  
  // Gérer le clic sur le bouton "Retour"
  const handleBack = () => {
    navigate('/admin/fuel/import/results');
  };
  
  // Gérer le clic sur le bouton "Nouvelle importation"
  const handleNewImport = () => {
    navigate('/admin/fuel/import');
  };
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton
            onClick={handleBack}
            sx={{ mr: 2 }}
            aria-label="retour"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Historique des importations
          </Typography>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Paper sx={{ mb: 3 }}>
          <ImportHistoryTable
            imports={imports}
            total={total}
            onViewDetails={handleViewDetails}
            onDownloadReport={handleDownloadReport}
            onReimport={handleReimport}
            onDelete={handleDelete}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Paper>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
          >
            Retour aux résultats
          </Button>
          
          <Button
            variant="contained"
            onClick={handleNewImport}
          >
            Nouvelle importation
          </Button>
        </Box>
      </Box>
      
      {/* Dialogue de confirmation de suppression */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Supprimer l'importation
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Êtes-vous sûr de vouloir supprimer cette importation ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="inherit">
            Annuler
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            autoFocus
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : undefined}
          >
            {deleting ? 'Suppression...' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FuelImportHistoryPage;